import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Gift, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/format";
import { SavedMethod } from "@/components/PayPalCheckout";

const NEON = "#E3FF00";
const priced = (p) => ["fixed", "package", "tiered"].includes(p.type) && p.price_hub_cents != null && (p.cycle || "month") === "month";

function ServiceBlock({ s, value, onPick }) {
  const free = s.plans.filter((p) => p.type === "free");
  const paid = s.plans.filter(priced);
  const soon = !paid.length && s.plans.some((p) => p.type !== "free");
  return (
    <div className="bg-white rounded-2xl border-2 border-neutral-900/10 p-5" data-testid={`choose-service-${s.key}`}>
      <h3 className="font-display text-lg font-bold">{s.label}</h3>
      {free.map((p) => <p key={p.id} className="text-sm text-emerald-700 flex items-center gap-1.5 mt-1"><Check className="h-4 w-4" /> {p.label} ({p.role}): gratis, sempre incluso</p>)}
      {paid.length > 0 && (
        <div className="grid gap-2 mt-3">
          <button onClick={() => onPick(null)} className={`text-left rounded-xl border-2 px-4 py-2.5 text-sm ${!value ? "border-neutral-900 bg-[#E3FF00]" : "border-neutral-200"}`} data-testid={`choose-none-${s.key}`}>Non mi serve</button>
          {paid.map((p) => (
            <button key={p.id} onClick={() => onPick(p.id)} className={`text-left rounded-xl border-2 px-4 py-2.5 text-sm flex justify-between gap-3 ${value === p.id ? "border-neutral-900 bg-[#E3FF00]" : "border-neutral-200"}`} data-testid={`choose-plan-${s.key}-${p.id}`}>
              <span><b>{p.label}</b> <span className="text-neutral-500">· {p.role}{p.tiers ? ` · ${p.tiers}` : ""}</span></span>
              <span className="text-right whitespace-nowrap"><b>{eur(p.price_hub_cents)}</b>/mese{p.price_site_cents > p.price_hub_cents && <span className="block text-xs text-neutral-500 line-through">{eur(p.price_site_cents)} sul sito</span>}</span>
            </button>
          ))}
        </div>
      )}
      {soon && <p className="text-sm text-neutral-500 mt-2">Piani a pagamento in arrivo.</p>}
    </div>
  );
}

function Summary({ q, pm, setPm, busy, onPay, trialEnd }) {
  const later = trialEnd && new Date(trialEnd) > new Date();
  return (
    <div className="bg-neutral-900 text-white rounded-3xl p-6 space-y-3 lg:sticky lg:top-6" data-testid="choose-summary">
      <h2 className="text-xl font-bold">Il tuo pacchetto</h2>
      {!q?.lines.length ? <p className="text-white/70 text-sm">Scegli almeno un servizio a pagamento.</p> : <>
        {q.lines.map((l) => <div key={l.service} className="flex justify-between text-sm"><span>{l.service_label} {l.plan_label}</span><span>{eur(l.price_cents)}</span></div>)}
        {q.discount_cents > 0 && <div className="flex justify-between text-sm" style={{ color: NEON }} data-testid="choose-discount"><span>Sconto combo {q.discount_percent}%</span><span>−{eur(q.discount_cents)}</span></div>}
        <div className="flex justify-between text-sm text-white/70"><span>IVA {q.vat_rate}%</span><span>{eur(q.vat_cents)}</span></div>
        <div className="flex justify-between text-2xl font-bold border-t border-white/20 pt-3"><span>Totale</span><span data-testid="choose-total">{eur(q.total_cents)}<span className="text-sm font-normal">/mese</span></span></div>
        {q.savings_cents > 0 && <p className="text-sm font-semibold rounded-lg px-3 py-2 text-neutral-900" style={{ backgroundColor: NEON }} data-testid="choose-savings">Risparmi {eur(q.savings_cents)} al mese rispetto ai singoli siti</p>}
        <div className="bg-white text-neutral-900 rounded-2xl p-3"><SavedMethod method={pm} onChange={setPm} /></div>
        {pm && <button disabled={busy} onClick={onPay} className="w-full rounded-xl py-3 font-semibold text-neutral-900 inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ backgroundColor: NEON }} data-testid="choose-pay">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{later ? `Conferma: primo addebito il ${new Date(trialEnd).toLocaleDateString("it-IT")}` : `Paga ${eur(q.total_cents)} e attiva`}</button>}
        <p className="text-[11px] text-white/60">Rinnovo mensile automatico, disdici quando vuoi. I servizi gratuiti restano sempre attivi.</p>
      </>}
    </div>
  );
}

export default function ChooseServices() {
  const { loadMe } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [pick, setPick] = useState({});
  const [q, setQ] = useState(null);
  const [pm, setPm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/hub/plans").then(({ data }) => {
      setD(data); setPm(data.payment_method);
      setPick(Object.fromEntries((data.current?.items || []).map((i) => [i.service, i.plan_id])));
    });
  }, []);
  useEffect(() => {
    const items = Object.entries(pick).filter(([, v]) => v).map(([service, plan_id]) => ({ service, plan_id }));
    api.post("/hub/quote", { items }).then(({ data }) => setQ(data));
  }, [pick]);
  if (!d) return null;
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hub/subscribe", { items: q.lines.map((l) => ({ service: l.service, plan_id: l.plan_id })) });
      toast.success(data.charged ? "Pagamento riuscito: servizi attivati" : "Servizi confermati: nessun addebito fino a fine prova");
      await loadMe(); navigate("/home");
    } catch (e) { toast.error(e?.response?.data?.detail || "Pagamento non riuscito"); }
    setBusy(false);
  };
  const trialLeft = d.trial_ends_at && new Date(d.trial_ends_at) > new Date();
  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="choose-services-page">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Scegli i tuoi servizi</h1>
        {trialLeft && <p className="mt-2 text-neutral-600 flex items-center gap-2" data-testid="choose-trial-info"><Gift className="h-4 w-4" /> Prova gratuita{d.campaign_name ? ` (evento ${d.campaign_name})` : ""} fino al {new Date(d.trial_ends_at).toLocaleDateString("it-IT")}: scegli ora, paghi solo dopo.</p>}
        {d.combo_discounts.length > 0 && <p className="text-sm text-neutral-600 mt-1">Sconti combo: {d.combo_discounts.map((c) => `${c.min_services}+ servizi −${c.percent}%`).join(" · ")}</p>}
      </div>
      <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
        <div className="grid sm:grid-cols-2 gap-4">{d.services.map((s) => <ServiceBlock key={s.key} s={s} value={pick[s.key]} onPick={(v) => setPick({ ...pick, [s.key]: v })} />)}</div>
        <Summary q={q} pm={pm} setPm={setPm} busy={busy} onPay={pay} trialEnd={d.trial_ends_at} />
      </div>
    </div>
  );
}
